import { useState, useEffect, useCallback, useRef } from "react";
import { useFormik } from "formik";
import { acquirerSchema } from "../../../../models/acquirer";
import { MOCK_DEFAULT_RATES, CARD_FLAGS } from "../constants";
import { acquirerDefaults } from "../../../../models/acquirer";
import { CardHelper } from "../../../../utils/helpers";
import { GLOBAL_STATUS } from "../../../../constants/common";

/**
 * useAcquirerForm
 * Hook dedicado à lógica do formulário de edição de Adquirentes.
 * Gerencia: Formik, seleção de bandeira, manipulação de rates.
 */
export const useAcquirerForm = (acquirer, onSave, onCancel, onChange) => {
    const [selectedFlagId, setSelectedFlagId] = useState("");
    const prevAcquirerRef = useRef(null);

    const formik = useFormik({
        initialValues: acquirerDefaults,
        validationSchema: acquirerSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando a adquirente selecionada muda
    // Usa setValues (padrão que funciona no Areas) em vez de resetForm
    useEffect(() => {
        if (acquirer) {
            // Evitar re-render infinito: só atualiza se a referência ou ID mudou
            const prevId = prevAcquirerRef.current?.id;
            const newId = acquirer.id;

            if (prevId !== newId || prevAcquirerRef.current === null) {
                formik.resetForm({
                    values: {
                        id: acquirer.id || "",
                        name: acquirer.name || "",
                        status: acquirer.status || GLOBAL_STATUS.ACTIVE,
                        rates: acquirer.rates || {},
                        anticipation: acquirer.anticipation || { enabled: false, type: "automatic", feePerMonth: 0, settlementDays: 1 },
                        idBankAccount: acquirer.idBankAccount || "",
                        bankAccountName: acquirer.bankAccountName || "",
                        createdAt: acquirer.createdAt || null,
                        createdBy: acquirer.createdBy || null
                    }
                });

                const keys = Object.keys(acquirer.rates || {});
                setSelectedFlagId(keys.length > 0 ? keys[0] : "");
            }

            prevAcquirerRef.current = acquirer;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [acquirer]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    /**
     * Cancela as edições e restaura o estado original da adquirente.
     */
    const handleCancel = useCallback(() => {
        if (acquirer) {
            formik.resetForm({
                values: {
                    id: acquirer.id || "",
                    name: acquirer.name || "",
                    status: acquirer.status || GLOBAL_STATUS.ACTIVE,
                    rates: acquirer.rates || {},
                    anticipation: acquirer.anticipation || { enabled: false, type: "automatic", feePerMonth: 0, settlementDays: 1 },
                    idBankAccount: acquirer.idBankAccount || "",
                    bankAccountName: acquirer.bankAccountName || "",
                    createdAt: acquirer.createdAt || null,
                    createdBy: acquirer.createdBy || null
                }
            });
            const keys = Object.keys(acquirer.rates || {});
            setSelectedFlagId(keys.length > 0 ? keys[0] : "");
        }
        if (onCancel) onCancel(); // Reseta isDirty no pai
    }, [acquirer, formik, onCancel]);

    /**
     * Remove uma bandeira do rates e ajusta a seleção.
     */
    const handleRemoveFlag = useCallback((flagId) => {
        const newRates = { ...formik.values.rates };
        delete newRates[flagId];
        formik.setFieldValue("rates", newRates);

        const remainingKeys = Object.keys(newRates);
        if (selectedFlagId === flagId) {
            setSelectedFlagId(remainingKeys.length > 0 ? remainingKeys[0] : "");
        }
    }, [formik, selectedFlagId]);

    /**
     * Gerencia a seleção multi-bandeira do react-select.
     * Adiciona novas flags com rates padrão e remove as desmarcadas.
     */
    const handleSelectChange = useCallback((newOptions) => {
        const currentRates = { ...formik.values.rates };
        const safeOptions = newOptions || [];
        const selectionValues = safeOptions.map(o => o.value);

        let flagToFocus = selectedFlagId;
        const newRates = {};

        // Processamos os selecionados para manter ou criar
        selectionValues.forEach(id => {
            const isPredefined = CARD_FLAGS.some(f => f.id === id);

            // ID limpo: lowercase, sem espaços extras, underscores no lugar de espaços
            const finalId = isPredefined ? id : id.toLowerCase().trim().replace(/\s+/g, '_');

            if (!currentRates[finalId]) {
                flagToFocus = finalId;
                newRates[finalId] = MOCK_DEFAULT_RATES(isPredefined ? finalId : 'visa');
            } else {
                newRates[finalId] = currentRates[finalId];
            }
        });

        formik.setFieldValue("rates", newRates);

        const finalIds = Object.keys(newRates);
        if (finalIds.length === 0) {
            setSelectedFlagId("");
        } else if (!finalIds.includes(flagToFocus)) {
            setSelectedFlagId(finalIds[0]);
        } else {
            setSelectedFlagId(flagToFocus);
        }
    }, [formik, selectedFlagId]);

    /**
     * Adiciona uma nova parcela de crédito à bandeira selecionada.
     */
    const handleAddInstallment = useCallback(() => {
        const currentCredit = formik.values.rates[selectedFlagId]?.credit || [];
        const nextInstallment = currentCredit.length > 0
            ? Math.max(...currentCredit.map(c => c.installments)) + 1
            : 1;
        formik.setFieldValue(`rates.${selectedFlagId}.credit`, [
            ...currentCredit,
            { installments: nextInstallment, rate: 0 }
        ]);
    }, [formik, selectedFlagId]);

    /**
     * Remove uma parcela de crédito pelo índice.
     */
    const handleRemoveInstallment = useCallback((index) => {
        const newCredit = [...(formik.values.rates[selectedFlagId]?.credit || [])];
        newCredit.splice(index, 1);
        formik.setFieldValue(`rates.${selectedFlagId}.credit`, newCredit);
    }, [formik, selectedFlagId]);

    /**
     * Restaura as taxas padrão para a bandeira selecionada.
     */
    const handleLoadDefaults = useCallback(() => {
        const isPredefined = CARD_FLAGS.some(f => f.id === selectedFlagId);
        formik.setFieldValue(`rates.${selectedFlagId}`, MOCK_DEFAULT_RATES(isPredefined ? selectedFlagId : 'visa'));
    }, [formik, selectedFlagId]);

    // Dados derivados (computados)
    const flagOptions = CARD_FLAGS.map(flag => ({
        value: flag.id,
        label: flag.name,
        icon: flag.icon
    }));

    const selectedOptions = Object.keys(formik.values.rates || {}).map(id => ({
        value: id,
        label: CardHelper.getFlagName(id, CARD_FLAGS),
        icon: CardHelper.getFlagIcon(id, CARD_FLAGS)
    }));

    return {
        formik,
        selectedFlagId,
        setSelectedFlagId,
        flagOptions,
        selectedOptions,
        actions: {
            handleSelectChange,
            handleRemoveFlag,
            handleAddInstallment,
            handleRemoveInstallment,
            handleLoadDefaults,
            handleCancel,
        }
    };
};
